import { useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ForegroundRefetch from '@/components/ForegroundRefetch';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Auth from "./pages/Auth";
import SignUp from "./pages/SignUp";
import Login from "./pages/Login";
import ResetPassword from './pages/ResetPassword';
import AuthConfirm from './pages/AuthConfirm';
import PaypalReturn from './pages/PaypalReturn';
import PersonaReturn from './pages/PersonaReturn';
import NativeUrlHandler from './components/NativeUrlHandler';
import Welcome from './pages/Welcome';
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";
import LandingPage from "./pages/LandingPage"; // kept for one-line rollback of SAB-426
import MarketingHome from "./pages/MarketingHome";
import Wallet from "./pages/Wallet";
import WalletDeposit from "./pages/WalletDeposit";
import WalletHistory from "./pages/WalletHistory";
import TermsPage from "./pages/legal/TermsPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import CookiesPage from "./pages/legal/CookiesPage";
import WithdrawalsPage from "./pages/legal/WithdrawalsPage";
import ChipTest from "./pages/ChipTest";
import MLBBeta from "./pages/MLBBeta";
import DraftRoom from "./pages/DraftRoom";
import { AuthProvider } from "./contexts/AuthContext";
import FreshdeskIdentity from "./components/FreshdeskIdentity";
import FreshdeskLoader from "./components/FreshdeskLoader";
import { ThemeProvider } from "./contexts/ThemeContext";
import { isIPad } from "./hooks/use-mobile";

// Set data-ipad on <html> so CSS can force mobile nav on all iPads
if (isIPad()) {
  document.documentElement.setAttribute('data-ipad', '');
}
import { RoleProvider } from "./contexts/RoleContext";
import { DraftSabotageProvider } from "./contexts/DraftSabotageContext";
import { SportProvider } from "./contexts/SportContext";
import { LeagueProvider } from "./contexts/LeagueContext";
import { RevealProvider } from "./contexts/RevealContext";
import { CommissionerActAsProvider } from "./contexts/CommissionerActAsContext";
import { CommissionerActingAsBanner } from "./components/mlb-beta/CommissionerActingAsBanner";
import { RevealOverlay } from "./components/nfl/bonuses/RevealOverlay";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingRouter from "./components/LandingRouter";
import ErrorBoundary from "./components/ErrorBoundary";
import GAPageTracker from "./components/GAPageTracker";
import { CookieConsentBanner } from "./components/CookieConsent";
import { AgeGate } from "./components/AgeGate";
import AnalyticsGate from "./components/CookieConsent/AnalyticsGate";
import LocationGate from "./components/CookieConsent/LocationGate";
import Footer from "./components/Footer";
import { ScreenOwnershipProvider, useScreenOwned } from "./contexts/ScreenOwnershipContext";
import AdminDashboard from "./pages/AdminDashboard";
import AdminGodMode from "./pages/AdminGodMode";
import AppSettings from "./pages/AppSettings";
import VerifyEligibility from "./pages/VerifyEligibility";
import GodModeDraft from "./pages/GodModeDraft";
import NFLStaging from "./pages/NFLStaging";
import NFLDemo from "./pages/NFLDemo";
import NFLCreateLeaguePage from "./pages/NFLCreateLeaguePage";
import NFLJoinLeaguePage from "./pages/NFLJoinLeaguePage";
import NFLStartChooser from "./pages/NFLStartChooser";
import MLBBetaJoinPage from "./pages/MLBBetaJoinPage";
import NFLLeaguesBrowsePage from "./pages/NFLLeaguesBrowsePage";
import NFLPublicJoinPage from "./pages/NFLPublicJoinPage";
import ResponsibleGaming from "./pages/ResponsibleGaming";
import ContestRules from "./pages/ContestRules";
import HowToPlay from "./pages/HowToPlay";
import Support from "./pages/Support";
import PromoRules from "./pages/PromoRules";
import StateAvailability from "./pages/StateAvailability";
// SAB-431: initGA() used to fire at module load. Now gated behind
// hasConsent('analytics') via <AnalyticsGate /> mounted below.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// SAB (scroll-reset): reset scroll to the top on every route change. React Router
// does not reset scroll on client navigation, so pages were inheriting the prior
// page's mid-page offset (compounded by scrollRestoration, now 'manual' — see
// main.tsx). Keyed on pathname. Skips when a hash is present so same-page anchor
// links (marketing nav #sections) are not fought. Resets window plus both possible
// document scrollers (documentElement / body) for webview/Safari.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.location.hash) return;
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  return null;
}

/** SAB-884: the footer, minus itself whenever a route has claimed the whole screen. */
function SiteFooter() {
  return useScreenOwned() ? null : <Footer />;
}

const App = () => (
  <ErrorBoundary>
  <QueryClientProvider client={queryClient}>
    {/* SAB-979: refresh on app foreground / tab focus. The client sets
        refetchOnWindowFocus:false globally, so a user returning from a PayPal or Persona
        browser hop would otherwise sit on a stale balance until they navigated. Inside the
        provider because it needs useQueryClient; outside the router because it is not a route. */}
    <ForegroundRefetch />
    <ThemeProvider>
    <TooltipProvider>
      <BrowserRouter>
      <ScreenOwnershipProvider>
        <ScrollToTop />
        {/* Build 6 Phase A: native custom-scheme return handler. Dormant on web and
            on native builds without @capacitor/app; no behavior change for anyone. */}
        <NativeUrlHandler />
        <GAPageTracker />
        {/* SAB-431: consent-gated analytics/marketing pixel init. Must mount
            before any feature that calls trackEvent so consent can be
            re-checked reactively when the user opts in mid-session. */}
        <AnalyticsGate />
        {/* SAB-466: consent-gated warm-load of the Locance geo scripts.
            No 'location' consent = no script injected. Does not prompt for
            location or gate anything — the money-moment hook (commit 5) does. */}
        <LocationGate />
        <AuthProvider>
          {/* SAB-916. The launcher itself is embedded in index.html (window.load, once, for
              the whole document). This mounts only the IDENTITY effect, and it lives here
              rather than in App itself for two reasons: App is an implicit-return arrow with
              no function body, so it cannot hold a hook without being restructured; and the
              effect calls useAuth(), so it must sit INSIDE AuthProvider. It renders null. */}
          <FreshdeskLoader />
          <FreshdeskIdentity />
          <SportProvider>
            <RoleProvider>
              <LeagueProvider>
              <CommissionerActAsProvider>
              <DraftSabotageProvider>
              <RevealProvider>
                <Toaster />
                <Sonner />
                <RevealOverlay />
                {/* Soap card-brand review: 18+ attestation for anonymous WEB visitors
                    only — never renders on native or for authenticated users (see
                    src/lib/ageGate.ts). */}
                <AgeGate />
                <CommissionerActingAsBanner />
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  {/* SAB-575: marketing-first auth entry. Public routes (never
                      ProtectedRoute — they ARE the entry). Commissioner intent
                      rides in ?intent=create-league. */}
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  {/* token_hash confirmation landing (confirm signup, change email).
                      Public: the user is not authed until verifyOtp runs here. */}
                  <Route path="/auth/confirm" element={<AuthConfirm />} />
                  {/* Build 6 Phase A: public PayPal-return bounce (https -> app scheme). */}
                  <Route path="/paypal-return" element={<PaypalReturn />} />
                  {/* Persona hosted-flow web return (redirect-uri = /persona-return). */}
                  <Route path="/persona-return" element={<PersonaReturn />} />
                  <Route path="/reset-pass" element={<Navigate to="/reset-password" replace />} />
                  <Route path="/welcome" element={<Welcome />} />
                  {/* SAB-587: authed landing gate. Resolves the fallback chain
                      (active NFL -> in_season MLB -> any NFL -> create/join) and
                      redirects. DEFAULT_AUTHED_HOME points here. */}
                  <Route path="/home" element={
                    <ProtectedRoute>
                      <LandingRouter />
                    </ProtectedRoute>
                  } />
                  <Route path="/beta" element={
                    <ProtectedRoute>
                      <MLBBeta />
                    </ProtectedRoute>
                  } />
                  <Route path="/mlbbeta" element={<Navigate to="/beta" replace />} />
                  <Route path="/draft-room/:leagueId" element={
                    <ProtectedRoute>
                      <DraftRoom />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin" element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/god-mode" element={
                    <ProtectedRoute>
                      <AdminGodMode />
                    </ProtectedRoute>
                  } />
                  <Route path="/app-settings" element={
                    <ProtectedRoute>
                      <AppSettings />
                    </ProtectedRoute>
                  } />
                  {/* SAB-493: post-signup state/age verification (closes the 4c
                      profile_incomplete loop; wraps the verify_age edge action). */}
                  <Route path="/verify-eligibility" element={
                    <ProtectedRoute>
                      <VerifyEligibility />
                    </ProtectedRoute>
                  } />
{/* SAB-522: real in-app legal pages. /terms + /cookies were
                      404; /privacy blank-redirected to Termly. Each embeds the
                      canonical Termly policy and (terms/privacy) the required
                      Aeropay Section 6 disclosure. */}
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/cookies" element={<CookiesPage />} />
                  <Route path="/withdrawals" element={<WithdrawalsPage />} />

                  <Route path="/install" element={<Install />} />
                  {/* Homepage gate — env-var driven. PROD sets
                      VITE_HOMEPAGE_VARIANT='marketing' (MarketingHome); unset / anything
                      else = LandingPage (one-env-var rollback). Both variants redirect an
                      AUTHENTICATED visitor to DEFAULT_AUTHED_HOME (/home -> LandingRouter);
                      MarketingHome gained that gate in SAB-654 (it was the "authed users see
                      the marketing page on app open" bug). Keep this comment in sync with the
                      actual Vercel value — a stale 'splash' claim here is what let SAB-654 recur. */}
                  <Route path="/" element={
                    import.meta.env.VITE_HOMEPAGE_VARIANT === 'marketing'
                      ? <MarketingHome />
                      : <LandingPage />
                  } />
                  <Route path="/god-mode/draft/:draftId" element={
                    <ProtectedRoute>
                      <GodModeDraft />
                    </ProtectedRoute>
                  } />
                  {/* CANONICAL. The draft tab picks its room per league from draft_status
                      (rule (e), src/lib/nflDraftRoomChoice.ts). */}
                  <Route path="/nfl" element={
                    <ProtectedRoute>
                      <NFLStaging />
                    </ProtectedRoute>
                  } />
                  {/* TRANSITION PATH, still serving the OLD room unconditionally.
                      Kept for links already out in the world -- emails sent, pushes
                      delivered, bookmarks -- and as the rollback surface. Deletable once
                      those age out; the native app follows the live site (server.url), so
                      nothing pins this into a shipped binary. */}
                  <Route path="/nfl/staging" element={
                    <ProtectedRoute>
                      <NFLStaging roomVariant="legacy" />
                    </ProtectedRoute>
                  } />
                  {/* The review path folds into the canonical one. Not deleted yet. */}
                  <Route path="/nfl/draft-v2" element={<Navigate to="/nfl?tab=draft" replace />} />
                  {/* SAB-642: cold-start chooser (rule 4 lands here, not create-only). */}
                  <Route path="/nfl/start" element={
                    <ProtectedRoute>
                      <NFLStartChooser />
                    </ProtectedRoute>
                  } />
                  <Route path="/nfl/create" element={
                    <ProtectedRoute>
                      <NFLCreateLeaguePage />
                    </ProtectedRoute>
                  } />
                  <Route path="/nfl/join/:inviteCode" element={<NFLJoinLeaguePage />} />
                  {/* SAB-642: MLB invite deep link (public, mirrors /nfl/join/:inviteCode). */}
                  <Route path="/beta/join/:code" element={<MLBBetaJoinPage />} />
                  {/* SAB-425: Public leagues browse + code-less join */}
                  <Route path="/nfl/leagues/browse" element={<NFLLeaguesBrowsePage />} />
                  <Route path="/nfl/join/browse/:leagueId" element={
                    <ProtectedRoute>
                      <NFLPublicJoinPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/responsible-gaming" element={<ResponsibleGaming />} />
                  {/* SAB-565 pt 1: account-level wallet + deposit (unbundled from league settings) */}
                  <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
                  <Route path="/wallet/deposit" element={<ProtectedRoute><WalletDeposit /></ProtectedRoute>} />
                  <Route path="/wallet/history" element={<ProtectedRoute><WalletHistory /></ProtectedRoute>} />
                  <Route path="/contest-rules" element={<ContestRules />} />
                  <Route path="/how-to-play" element={<HowToPlay />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/promo-rules" element={<PromoRules />} />
                  <Route path="/help" element={<Navigate to="/support" replace />} />
                  <Route path="/state-availability" element={<StateAvailability />} />
                  {/* SAB-432: companion footer/TrustLegality links (4 places, was 404). */}
                  <Route path="/nfldemo" element={<NFLDemo />} />
                  <Route path="/_chip-test" element={<ChipTest />} />
                  {/* SAB-522: redirects for legacy/wrong paths that homepage +
                      marketing links pointed at (also catches bookmarks). The
                      hrefs themselves are corrected at source too. */}
                  <Route path="/login" element={<Navigate to="/auth" replace />} />
                  <Route path="/nfl/leagues/create" element={<Navigate to="/nfl/create" replace />} />
                  <Route path="/nfl/leagues/join" element={<Navigate to="/nfl/leagues/browse" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                {/* SAB-431: site-wide footer (legal + manage cookies link).
                    SAB-429 homepage redesign will likely enhance this.

                    SAB-884: it is a SIBLING of <Routes>, so it renders on top of whatever a
                    route drew -- including a route that needs the entire viewport. The draft
                    room is 100dvh; the footer below it made the document 299px taller than
                    the screen on a phone and 78px on desktop, which is why nothing in that
                    room stayed pinned. A route now declares ownership and this steps aside.

                    The CONSENT BANNER below is deliberately NOT gated the same way: a
                    first-time user deep-linked straight into the room would otherwise be
                    able to finish a session without ever being shown it. */}
                <SiteFooter />
              </RevealProvider>
              </DraftSabotageProvider>
              </CommissionerActAsProvider>
              </LeagueProvider>
            </RoleProvider>
          </SportProvider>
        </AuthProvider>
        {/* SAB-431: consent banner. Rendered at root so it sits above all
            page content; returns null when needsBanner() is false. */}
        <CookieConsentBanner />
      </ScreenOwnershipProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
